import { zodResolver } from '@hookform/resolvers/zod';
import { DISPLAY_NAME_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@iculogbook/shared';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';

import { ApiError, NetworkError } from '../auth/api.ts';
import { AuthLayout, FormError } from '../auth/AuthLayout.tsx';
import { useAuth } from '../auth/AuthProvider.tsx';
import { errorCopy, OFFLINE_COPY } from '../auth/errorCopy.ts';
import { Button, Field, PasswordInput, TextInput } from '../components/index.ts';

const schema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Enter your email address.')
    .email('Enter a valid email address.'),
  password: z
    .string()
    .min(
      PASSWORD_MIN_LENGTH,
      `Passwords need to be at least ${String(PASSWORD_MIN_LENGTH)} characters.`,
    ),
  displayName: z
    .string()
    .trim()
    .min(1, 'Enter the name that should appear on your PDFs.')
    .max(DISPLAY_NAME_MAX_LENGTH, `Keep this under ${String(DISPLAY_NAME_MAX_LENGTH)} characters.`),
});

type Values = z.infer<typeof schema>;

export function RegisterRoute() {
  const { register: createAccount } = useAuth();
  const navigate = useNavigate();
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', displayName: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await createAccount(values);
      void navigate('/entries', { replace: true });
    } catch (error) {
      setFormError(
        error instanceof NetworkError
          ? OFFLINE_COPY
          : errorCopy(error instanceof ApiError ? error.code : null),
      );
    }
  });

  return (
    <AuthLayout
      title="Create an account"
      subtitle="A logbook for UK Intensive Care Medicine trainees."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/login" className="text-accent underline hover:text-accent-hover">
            Sign in
          </Link>
        </>
      }
    >
      <form onSubmit={(event) => void onSubmit(event)} className="flex flex-col gap-4" noValidate>
        <FormError message={formError} />

        <Field label="Email" error={errors.email?.message}>
          <TextInput
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            {...register('email')}
          />
        </Field>

        <Field
          label="Name"
          help="Appears on the PDFs you generate, so write it as a supervisor should see it."
          error={errors.displayName?.message}
        >
          <TextInput autoComplete="name" placeholder="Dr A Trainee" {...register('displayName')} />
        </Field>

        <Field
          label="Password"
          // Stated up front, not sprung as an error after submitting.
          help={`At least ${String(PASSWORD_MIN_LENGTH)} characters. A few unrelated words works well and is easier to remember than a short, complicated one.`}
          error={errors.password?.message}
        >
          <PasswordInput autoComplete="new-password" {...register('password')} />
        </Field>

        <Button type="submit" variant="primary" block loading={isSubmitting}>
          Create account
        </Button>

        {/* Required adjacent to the submit button — see COMPLIANCE.md §2. */}
        <p className="text-center text-xs text-faint">
          ICULogbook holds no patient-identifiable information. Read the{' '}
          <Link to="/privacy" className="underline hover:text-text">
            privacy notice
          </Link>
          .
        </p>
      </form>
    </AuthLayout>
  );
}
